import React, { useEffect, useState, Fragment } from 'react';
import { CheckIcon, XIcon, EyeIcon, ShieldCheckIcon, SaveIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { Role, Permission, PermissionLevel, normalizeRoleName } from '../../types';
export function Permissions() {
  const {
    roles,
    permissions,
    getPermissionsByRole,
    updatePermissions
  } = useData();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [rolePermissions, setRolePermissions] = useState<Record<string, Permission>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const modules = [
  // --- Panel de Control ---
  {
    key: 'dashboard',
    label: 'Panel de Control (General)',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_calendar',
    label: '↳ Calendario y Citas del Día',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_stats',
    label: '↳ Estadísticas y Gráficos',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_pipeline',
    label: '↳ Pipeline de Ingresos',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_appointments_by_user',
    label: '↳ Citas Creadas por Usuario',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_revenue_user',
    label: '↳ Ingresos por Usuario',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_revenue_doctor',
    label: '↳ Ingresos por Doctor',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_recent_appointments',
    label: '↳ Citas Recientes',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_service_detail',
    label: '↳ Detalle por Servicio',
    section: 'Panel de Control'
  },
  // --- Módulos Principales ---
  {
    key: 'patients',
    label: 'Pacientes',
    section: 'Módulos Principales'
  }, {
    key: 'patients_insurance',
    label: '↳ Ver/Editar Seguro Médico',
    section: 'Módulos Principales'
  }, {
    key: 'doctors',
    label: 'Doctores',
    section: 'Módulos Principales'
  }, {
    key: 'appointments',
    label: 'Citas',
    section: 'Módulos Principales'
  }, {
    key: 'clinicalHistory',
    label: 'Historial Clínico',
    section: 'Módulos Principales'
  },
  // --- Servicios ---
  {
    key: 'services',
    label: 'Servicios (Gestión)',
    section: 'Servicios'
  }, {
    key: 'services_stats',
    label: '↳ Estadísticas y Reportes',
    section: 'Servicios'
  },
  // --- Catálogos ---
  {
    key: 'insurance',
    label: 'Seguros',
    section: 'Catálogos'
  }, {
    key: 'specialties',
    label: 'Especialidades',
    section: 'Catálogos'
  },
  // --- CRM ---
  {
    key: 'contacts',
    label: 'Contactos',
    section: 'CRM'
  }, {
    key: 'companies',
    label: 'Empresas',
    section: 'CRM'
  }, {
    key: 'opportunities',
    label: 'Oportunidades',
    section: 'CRM'
  },
  // --- Seguimiento y Auditoría ---
  {
    key: 'followUpNotes',
    label: 'Notas de Seguimiento',
    section: 'Seguimiento y Auditoría'
  }, {
    key: 'auditLog',
    label: 'Bitácora',
    section: 'Seguimiento y Auditoría'
  },
  // --- Inteligencia ---
  {
    key: 'intelligence',
    label: 'Inteligencia y Analítica',
    section: 'Inteligencia'
  },
  // --- Comisiones ---
  {
    key: 'commissions',
    label: 'Comisiones',
    section: 'Comisiones'
  },
  // --- Configuración ---
  {
    key: 'settings',
    label: 'Configuración (General)',
    section: 'Configuración'
  }, {
    key: 'settings_profile',
    label: '↳ Perfil',
    section: 'Configuración'
  }, {
    key: 'settings_preferences',
    label: '↳ Preferencias',
    section: 'Configuración'
  }, {
    key: 'settings_notifications',
    label: '↳ Notificaciones',
    section: 'Configuración'
  }, {
    key: 'settings_security',
    label: '↳ Seguridad',
    section: 'Configuración'
  }, {
    key: 'settings_email',
    label: '↳ Correos',
    section: 'Configuración'
  }, {
    key: 'settings_users',
    label: '↳ Usuarios',
    section: 'Configuración'
  }, {
    key: 'settings_roles_permissions',
    label: '↳ Roles y Permisos',
    section: 'Configuración'
  }];
  // Group modules by section
  const groupedModules = modules.reduce((acc, module) => {
    const section = module.section || 'Otros';
    if (!acc[section]) acc[section] = [];
    acc[section].push(module);
    return acc;
  }, {} as Record<string, typeof modules>);
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.displayName
  }));
  useEffect(() => {
    if (selectedRoleId) {
      const perms = getPermissionsByRole(selectedRoleId);
      const permsMap: Record<string, Permission> = {};
      // Initialize with existing permissions
      if (perms.length > 0) {
        perms.forEach(p => {
          permsMap[p.module] = p;
        });
      }
      // Ensure all modules have an entry
      modules.forEach(m => {
        if (!permsMap[m.key]) {
          permsMap[m.key] = {
            id: crypto.randomUUID(),
            roleId: selectedRoleId,
            module: m.key,
            canCreate: false,
            canRead: false,
            canUpdate: false,
            canDelete: false,
            permissionLevel: 'none' as PermissionLevel,
            createdAt: new Date(),
            updatedAt: new Date()
          };
        }
      });
      setRolePermissions(permsMap);
      setHasChanges(false);
    }
  }, [selectedRoleId, permissions]);
  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return <CheckIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-3087" />;
      case 'read':
        return <EyeIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" data-id="element-3088" />;
      case 'own':
        return <ShieldCheckIcon className="w-5 h-5 text-amber-500 dark:text-amber-400" data-id="element-3089" />;
      case 'none':
        return <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-3090" />;
    }
  };
  const getPermissionLabel = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return 'Completo';
      case 'read':
        return 'Solo lectura';
      case 'own':
        return 'Propias';
      case 'none':
        return 'Sin acceso';
    }
  };
  const cyclePermission = (module: string) => {
    const levels: PermissionLevel[] = ['none', 'read', 'own', 'full'];
    const currentLevel = rolePermissions[module]?.permissionLevel || 'none';
    const nextLevel = levels[(levels.indexOf(currentLevel) + 1) % levels.length];
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...(prev[module] || {
          id: crypto.randomUUID(),
          roleId: selectedRoleId,
          module,
          createdAt: new Date(),
          updatedAt: new Date()
        }),
        permissionLevel: nextLevel,
        canCreate: nextLevel === 'full',
        canRead: nextLevel !== 'none',
        canUpdate: nextLevel === 'full' || nextLevel === 'own',
        canDelete: nextLevel === 'full'
      }
    }));
    setHasChanges(true);
  };
  const handleSave = async () => {
    if (!selectedRoleId) return;
    try {
      await updatePermissions(selectedRoleId, Object.values(rolePermissions));
      const selectedRole = roles.find(r => r.id === selectedRoleId);
      if (selectedRole) {
        const saved = localStorage.getItem('savidna-role-permissions');
        const allPermissions = saved ? JSON.parse(saved) : {};
        // Normalizamos para que "Recepción" se guarde como "recepcion"
        const normalizedName = normalizeRoleName(selectedRole.name);
        modules.forEach(m => {
          if (!allPermissions[m.key]) allPermissions[m.key] = {};
          // Guardamos el nivel (read, full, etc.) para este rol específico
          allPermissions[m.key][normalizedName] = rolePermissions[m.key]?.permissionLevel || 'none';
        });
        localStorage.setItem('savidna-role-permissions', JSON.stringify(allPermissions));
        // VITAL: Avisa al Dashboard que los permisos cambiaron
        window.dispatchEvent(new Event('permissions-updated'));
        setHasChanges(false);
        setSaveMessage('✅ Permisos guardados correctamente');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      setSaveMessage('Error al guardar permisos');
    }
  };
  const selectedRole = roles.find(r => r.id === selectedRoleId);
  return <div className="space-y-6" data-id="element-3091">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3092">
        <div data-id="element-3093">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-3094">
            <LockIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" data-id="element-3095" />
            Matriz de Permisos
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-3096">
            Configura el nivel de acceso para cada módulo
          </p>
        </div>
        {hasChanges && <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3098" />} onClick={handleSave} data-id="element-3097">
            Guardar Cambios
          </Button>}
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-3099">
          {saveMessage}
        </div>}

      <Card className="p-6" data-id="element-3100">
        <div className="max-w-xl" data-id="element-3101">
          <Select label="Seleccionar Rol a Configurar" options={roleOptions} value={selectedRoleId} onChange={setSelectedRoleId} placeholder="Seleccione un rol..." data-id="element-3102" />
          {selectedRole && <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl flex items-start gap-3" data-id="element-3103">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5" data-id="element-3104" />
              <div data-id="element-3105">
                <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200" data-id="element-3106">
                  Editando: {selectedRole.displayName}
                </p>
                <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1" data-id="element-3107">
                  {selectedRole.description || 'Sin descripción'}
                </p>
              </div>
            </div>}
        </div>
      </Card>

      {selectedRoleId ? <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3108">
            {[{
          icon: CheckIcon,
          label: 'Acceso completo',
          color: 'text-green-500'
        }, {
          icon: EyeIcon,
          label: 'Solo lectura',
          color: 'text-blue-500'
        }, {
          icon: ShieldCheckIcon,
          label: 'Solo propias',
          color: 'text-amber-500'
        }, {
          icon: XIcon,
          label: 'Sin acceso',
          color: 'text-red-500'
        }].map((item, i) => <div key={i} className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm" data-id="element-3109">
                <item.icon className={`w-5 h-5 ${item.color}`} data-id="element-3110" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300" data-id="element-3111">
                  {item.label}
                </span>
              </div>)}
          </div>

          <Card className="overflow-hidden" data-id="element-3112">
            <div className="overflow-x-auto" data-id="element-3113">
              <table className="w-full text-sm text-left" data-id="element-3114">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3115">
                  <tr data-id="element-3116">
                    <th className="px-6 py-4 font-semibold" data-id="element-3117">Módulo</th>
                    <th className="px-6 py-4 text-center font-semibold" data-id="element-3118">
                      Nivel de Acceso
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-3119">
                      Crear
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-3120">
                      Leer
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-3121">
                      Editar
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-3122">
                      Eliminar
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3123">
                  {Object.entries(groupedModules).map(([sectionName, sectionModules]) => <Fragment key={sectionName} data-id="element-3124">
                        <tr className="bg-gray-50/50 dark:bg-gray-800/30" data-id="element-3125">
                          <td colSpan={6} className="px-6 py-3" data-id="element-3126">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400" data-id="element-3127">
                              {sectionName}
                            </span>
                          </td>
                        </tr>
                        {sectionModules.map(module => {
                  const permission = rolePermissions[module.key];
                  const level = permission?.permissionLevel || 'none';
                  const isSubModule = module.label.startsWith('↳');
                  return <tr key={module.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-3128">
                              <td className={`px-6 py-4 font-medium text-gray-900 dark:text-white ${isSubModule ? 'pl-10 text-gray-500' : ''}`} data-id="element-3129">
                                {module.label}
                              </td>
                              <td className="px-6 py-4 text-center" data-id="element-3130">
                                <button onClick={() => cyclePermission(module.key)} className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-transparent hover:border-gray-200 dark:hover:border-gray-600 w-full max-w-[140px] mx-auto" data-id="element-3131">
                                  {getPermissionIcon(level)}
                                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300" data-id="element-3132">
                                    {getPermissionLabel(level)}
                                  </span>
                                </button>
                              </td>
                              {['canCreate', 'canRead', 'canUpdate', 'canDelete'].map(key => <td key={key} className="px-6 py-4 text-center" data-id="element-3133">
                                  {permission?.[key as keyof Permission] ? <CheckIcon className="w-4 h-4 text-green-500 mx-auto" data-id="element-3134" /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 mx-auto" data-id="element-3135" />}
                                </td>)}
                            </tr>;
                })}
                      </Fragment>)}
                </tbody>
              </table>
            </div>
          </Card>
        </> : <div className="py-12 text-center text-gray-400 bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-3136">
          <ShieldCheckIcon className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-3137" />
          <p data-id="element-3138">
            Selecciona un rol arriba para comenzar a configurar sus permisos
          </p>
        </div>}
    </div>;
}